import { useState, useEffect } from 'react';
import { Shield, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductDemoModal from '@/components/Common/ProductDemoModal';

// API services
import { fetchPartners } from '@/services/partnersService';
import { fetchFeaturedVideo } from '@/services/featuredVideoService';
import { fetchCarousel } from '@/services/carouselService';

export default function AboutCTA() {
  const [current, setCurrent] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // === CAROUSEL (API) ===
  const [carouselImages, setCarouselImages] = useState<
    { id: number; imageUrl: string; order: number }[]
  >([]);
  const [loadingCarousel, setLoadingCarousel] = useState(true);

  // === PARTNERS (API) ===
  const [partners, setPartners] = useState<
    { id: number; imageUrl: string; order: number }[]
  >([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  // === FEATURED VIDEO (API) ===
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [sourceType, setSourceType] = useState<'youtube' | 'upload' | null>(
    null,
  );
  const [loadingVideo, setLoadingVideo] = useState(true);

  // --- Load Featured Video ---
  useEffect(() => {
    async function loadVideo() {
      try {
        const data = await fetchFeaturedVideo();
        if (data?.videoUrl) {
          setVideoUrl(data.videoUrl);
          setSourceType(data.source);
        }
      } catch (e) {
        console.error('Failed to load featured video', e);
      } finally {
        setLoadingVideo(false);
      }
    }
    loadVideo();
  }, []);

  // --- Load Carousel Images ---
  useEffect(() => {
    async function loadCarousel() {
      try {
        const data = await fetchCarousel();
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) => a.order - b.order);
          setCarouselImages(sorted);
        }
      } catch (err) {
        console.error('Failed to load carousel images', err);
      } finally {
        setLoadingCarousel(false);
      }
    }
    loadCarousel();
  }, []);

  // --- Load Partners ---
  useEffect(() => {
    async function loadPartners() {
      try {
        const data = await fetchPartners();
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) => a.order - b.order);
          setPartners(sorted);
        }
      } catch (err) {
        console.error('Failed to load partners', err);
      } finally {
        setLoadingPartners(false);
      }
    }
    loadPartners();
  }, []);

  // --- Carousel Auto-Slide ---
  useEffect(() => {
    if (carouselImages.length === 0) return;

    const interval = setInterval(
      () => setCurrent((prev) => (prev + 1) % carouselImages.length),
      5000,
    );
    return () => clearInterval(interval);
  }, [carouselImages]);

  return (
    <>
      <section
        className="mx-auto max-w-7xl bg-white px-6 py-20"
        data-aos="fade-up"
      >
        {/* Heading */}
        <div data-aos="fade-up" className="text-center">
          <h2 className="text-3xl font-extrabold tracking-tighter text-[#0B0B0B] sm:text-4xl">
            Advance Philippine Defense with Us
          </h2>
          <p className="mx-auto mt-3 max-w-3xl text-sm font-medium tracking-tighter text-black">
            Your partnership can spark innovation, accelerate breakthroughs, and
            help build a safer, stronger Philippines.
          </p>
        </div>

        {/* Layout */}
        <div className="mx-auto mt-12 flex max-w-5xl flex-col items-center gap-10 md:flex-row md:gap-16 2xl:max-w-7xl">
          {/* === Carousel (API-based) === */}
          <div
            data-aos="fade-left"
            className="relative order-1 h-[250px] w-full overflow-hidden rounded-lg shadow-lg sm:h-[320px] md:order-2 md:h-[420px] md:w-[60%] lg:h-[340px]"
          >
            {/* Loading Skeleton */}
            {loadingCarousel ? (
              <div className="absolute inset-0 animate-pulse bg-gray-200" />
            ) : null}

            {/* If no images */}
            {!loadingCarousel && carouselImages.length === 0 && (
              <div className="flex h-full items-center justify-center bg-gray-100 text-gray-500">
                No carousel images available.
              </div>
            )}

            {/* Actual Slides */}
            {carouselImages.map((slide, idx) => (
              <img
                key={slide.id}
                src={slide.imageUrl}
                alt={`Slide ${idx + 1}`}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                  current === idx ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}

            {/* Fade Overlay */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />

            {/* Indicators */}
            {!loadingCarousel && carouselImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                {carouselImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrent(idx)}
                    className={`h-2 w-6 rounded-full transition-all ${
                      current === idx ? 'bg-[#FFBD59]' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* === Right Section === */}
          <div
            data-aos="fade-right"
            className="order-2 w-full max-w-5xl md:order-1 md:w-[40%]"
          >
            <p className="text-sm leading-relaxed text-black">
              At AeroComp, we believe that lasting impact is built through
              strong partnerships — working with the Philippine Air Force, Navy,
              Army, and leading industry innovators.
            </p>

            <p className="mt-4 text-sm leading-relaxed text-gray-700">
              Together, we scale research, accelerate production, and push
              forward world-class defense solutions—strengthening national
              self-reliance.
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 flex w-full items-stretch gap-3">
              <Link to="/products" className="flex-1">
                <button className="h-12 w-full rounded-md border border-[#FFBD59] bg-[#FFBD59] text-sm font-semibold text-black transition hover:bg-[#e6a949]">
                  <span className="flex items-center justify-center gap-2">
                    <Shield size={18} /> See Products
                  </span>
                </button>
              </Link>

              <button
                onClick={() => setIsModalOpen(true)}
                disabled={loadingVideo}
                className="h-12 flex-1 rounded-md border-2 border-black bg-transparent text-sm font-semibold text-black transition hover:bg-[#FFF3DC]"
              >
                <span className="flex items-center justify-center gap-2">
                  <Play size={16} /> Product Demo
                </span>
              </button>
            </div>

            {/* Partners */}
            <div data-aos="fade-up" className="mt-8">
              <p className="text-center text-sm font-medium text-gray-700">
                In Partnership with:
              </p>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                {/* Partner Skeleton */}
                {loadingPartners
                  ? [1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-10 w-20 animate-pulse rounded bg-gray-200"
                      />
                    ))
                  : null}

                {/* Actual Partners */}
                {!loadingPartners &&
                  partners.map((p) => (
                    <img
                      key={p.id}
                      src={p.imageUrl}
                      className="h-10 w-auto object-contain"
                    />
                  ))}

                {/* Fallback */}
                {!loadingPartners && partners.length === 0 && (
                  <p className="text-xs text-gray-400">
                    No partner logos available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === PRODUCT DEMO MODAL === */}
      <ProductDemoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoUrl={videoUrl}
        source={sourceType}
      />
    </>
  );
}
